import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import { generatePalleLabel, generateCMR } from '../services/pdf';
import Header from '../components/Header';
import BookingDetail from '../components/BookingDetail';
import AnnouncementBanner from '../components/AnnouncementBanner';
import './Dashboard.css';

function RowMenu({ order, onAction }) {
  const [open, setOpen] = useState(false);

  const handle = (action) => { onAction(action, order); setOpen(false); };

  return (
    <span className="row-action-wrap">
      <span className="row-action-label">Handling ▾</span>
      <select
        className="row-action-select"
        value=""
        onChange={(e) => { if (e.target.value) { handle(e.target.value); e.target.value = ''; } }}
      >
        <option value="">Vælg...</option>
        {!order.locked && <option value="edit">{order.approval_status === 'APPROVED' ? 'Anmod ændring' : 'Rediger'}</option>}
        <option value="copy">Kopier</option>
        <option value="palle">Pallelabel</option>
        <option value="cmr">Fragtbrev</option>
      </select>
    </span>
  );
}

function Dashboard() {
  const [activeOrders, setActiveOrders] = useState([]);
  const [pastOrders, setPastOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dashboard_widgets') || '{}'); } catch { return {}; }
  });
  const [showWidgetMenu, setShowWidgetMenu] = useState(false);
  const { token, user } = useAuth();
  const isViewer = user?.role === 'viewer';

  const toggleWidget = (key) => {
    const next = { ...widgetConfig, [key]: !isWidgetVisible(key) };
    setWidgetConfig(next);
    localStorage.setItem('dashboard_widgets', JSON.stringify(next));
  };
  const isWidgetVisible = (key) => widgetConfig[key] !== false; // default visible
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    try {
      const [activeData, pastData] = await Promise.all([
        api.getActiveBookings(),
        api.getPastBookings(),
      ]);

      const mapBookings = (bookings) =>
        (bookings || []).map((b) => ({
          ...b,
          reference: b.booking_reference || `#${b.booking_id}`,
          sender_name: b.pickup_name,
          recipient_name: b.delivery_name,
        }));

      setActiveOrders(mapBookings(activeData.bookings || activeData));
      setPastOrders(mapBookings(pastData.bookings || pastData));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const getStatusLabel = (order) => {
    const approval = order.approval_status || order.approvalStatus;
    if (approval === 'PENDING_APPROVAL') return t('bookingPending');
    if (approval === 'APPROVED') return t('bookingApproved');
    if (approval === 'REJECTED') return t('bookingRejected');
    if (order.status) return order.status;
    return t('bookingAccepted');
  };

  const getStatusClass = (order) => {
    const approval = order.approval_status || order.approvalStatus;
    if (approval === 'PENDING_APPROVAL') return 'status-badge status-pending';
    if (approval === 'REJECTED') return 'status-badge status-rejected';
    return 'status-badge';
  };

  const filterOrders = (orders) => {
    if (!searchTerm) return orders;
    const s = searchTerm.toLowerCase();
    return orders.filter(o =>
      o.reference?.toLowerCase().includes(s) ||
      o.sender_name?.toLowerCase().includes(s) ||
      o.recipient_name?.toLowerCase().includes(s) ||
      o.booking_reference?.toLowerCase().includes(s) ||
      o.pickup_name?.toLowerCase().includes(s) ||
      o.delivery_name?.toLowerCase().includes(s)
    );
  };

  const handleRowAction = (action, order) => {
    switch (action) {
      case 'edit': navigate(`/book?edit=${order.id}`); break;
      case 'copy': navigate(`/book?copy=${order.id}`); break;
      case 'palle': generatePalleLabel(order).save(`pallelabel-${order.reference || order.id}.pdf`); break;
      case 'cmr': generateCMR(order).save(`fragtbrev-${order.reference || order.id}.pdf`); break;
      default: break;
    }
  };

  const filteredActive = filterOrders(activeOrders);
  const filteredPast = filterOrders(pastOrders);

  return (
    <div className="dashboard">
      <Header />

      <div className="dashboard-nav">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            {t('overview')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/orders' ? 'active' : ''}`}
            onClick={() => navigate('/orders')}
          >
            {t('orders')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/calendar' ? 'active' : ''}`}
            onClick={() => navigate('/calendar')}
          >
            {t('calendar')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/insights' ? 'active' : ''}`}
            onClick={() => navigate('/insights')}
          >
            {t('insights')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/administration' ? 'active' : ''}`}
            onClick={() => navigate('/administration')}
          >
            {t('administration')}
          </button>
        </div>

        <div className="nav-right">
          <div className="widget-menu-wrapper">
            <button className="btn-shortcuts" onClick={() => setShowWidgetMenu(!showWidgetMenu)}>
              Widgets
            </button>
            {showWidgetMenu && (
              <div className="widget-menu">
                {[
                  { key: 'active', label: t('activeWidget') },
                  { key: 'past', label: t('pastWidget') },
                  { key: 'sidebar', label: t('sidebarWidget') },
                ].map(w => (
                  <label key={w.key} className="widget-option">
                    <input type="checkbox" checked={isWidgetVisible(w.key)} onChange={() => toggleWidget(w.key)} />
                    {w.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <button
            className="btn-shortcuts"
            onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { altKey: true, key: 'k' }))}
            title="Genvejstaster (Alt+K)"
          >
            Genveje
          </button>

          <div className="search-box">
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>

          {!isViewer && (
            <>
              <button className="btn-import-order" onClick={() => navigate('/import')}>
                Import
              </button>
              <button className="btn-book-order" onClick={() => navigate('/book')}>
                {t('bookOrder')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="dashboard-content">
        <div className="orders-section">
          {isWidgetVisible('active') && <div className="orders-column">
            <h3 className="orders-title">{t('activeOrders')}</h3>
            <div className="orders-list">
              {loading ? (
                <p className="loading-text">{t('loading')}</p>
              ) : filteredActive.length === 0 ? (
                <p className="empty-text">{t('noActiveOrders')}</p>
              ) : (
                <table className="overview-table">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('ref')}</th>
                      <th>{t('from')}</th>
                      <th>{t('to')}</th>
                      <th>{t('statusCol')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActive.map((order) => (
                      <tr key={order.id} className="clickable-row" onClick={() => setSelectedOrder(order)}>
                        <td className="ot-date">{formatDate(order.pickup_date)} {formatTime(order.pickup_time)}</td>
                        <td className="ot-ref">{order.reference}</td>
                        <td className="ot-route">{order.sender_name}</td>
                        <td className="ot-route">{order.recipient_name}</td>
                        <td>
                          <span className={getStatusClass(order)} title={order.rejection_reason || ''}>{getStatusLabel(order)}</span>
                          {order.rejection_reason && <div className="rejection-reason">{order.rejection_reason}</div>}
                        </td>
                        <td className="ot-actions">
                          {!isViewer && <RowMenu order={order} onAction={handleRowAction} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>}

          {isWidgetVisible('past') && <div className="orders-column">
            <div className="orders-title-row">
              <h3 className="orders-title">{t('pastOrders')}</h3>
              <a href="#" className="see-all-link" onClick={(e) => { e.preventDefault(); navigate('/orders'); }}>
                {t('seeAllPastOrders')}
              </a>
            </div>
            <div className="orders-list">
              {loading ? (
                <p className="loading-text">{t('loading')}</p>
              ) : filteredPast.length === 0 ? (
                <p className="empty-text">{t('noPastOrders')}</p>
              ) : (
                <table className="overview-table">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('ref')}</th>
                      <th>{t('from')}</th>
                      <th>{t('to')}</th>
                      <th>{t('statusCol')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPast.map((order) => (
                      <tr key={order.id} className="clickable-row" onClick={() => setSelectedOrder(order)}>
                        <td className="ot-date">{formatDate(order.pickup_date)} {formatTime(order.pickup_time)}</td>
                        <td className="ot-ref">{order.reference}</td>
                        <td className="ot-route">{order.sender_name}</td>
                        <td className="ot-route">{order.recipient_name}</td>
                        <td>
                          <span className={getStatusClass(order)} title={order.rejection_reason || ''}>{getStatusLabel(order)}</span>
                          {order.rejection_reason && <div className="rejection-reason">{order.rejection_reason}</div>}
                        </td>
                        <td className="ot-actions">
                          {!isViewer && <RowMenu order={order} onAction={handleRowAction} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="load-more">
              <span className="load-more-icon">▼</span>
            </div>
          </div>}
        </div>

        {isWidgetVisible('sidebar') && <div className="sidebar">
          <AnnouncementBanner />
          <div className="sidebar-card">
            <h4 className="sidebar-title">{t('benefitsTitle')}</h4>

            <div className="benefit-item">
              <div className="benefit-header">
                <span className="benefit-icon">📄</span>
                <span className="benefit-name">{t('selfService')}</span>
              </div>
              <p className="benefit-desc">{t('selfServiceDesc')}</p>
            </div>

            <div className="benefit-item">
              <div className="benefit-header">
                <span className="benefit-icon">🖱️</span>
                <span className="benefit-name">{t('easyBooking')}</span>
              </div>
              <p className="benefit-desc">{t('easyBookingDesc')}</p>
            </div>

            <div className="benefit-item">
              <div className="benefit-header">
                <span className="benefit-icon">👤</span>
                <span className="benefit-name">{t('customerFocus')}</span>
              </div>
              <p className="benefit-desc">{t('customerFocusDesc')}</p>
            </div>

            <div className="benefit-item">
              <div className="benefit-header">
                <span className="benefit-icon">🚀</span>
                <span className="benefit-name">{t('development')}</span>
              </div>
              <p className="benefit-desc">{t('developmentDesc')}</p>
            </div>
          </div>
        </div>}
      </div>
      {selectedOrder && <BookingDetail order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
    </div>
  );
}

export default Dashboard;
